import React, { useState } from "react";
import { Gift, Check } from "lucide-react";
import { useCart } from "../../context/CartContext";
import { euro } from "../../utils/format";
import { SITE_IMAGES } from "../../data/siteImages";
import PlantImage from "../../components/fancyplants/PlantImage";
import Seo from "../../components/fancyplants/Seo";

const AMOUNTS = [25, 50, 75, 100];

export default function GiftCardsPage() {
  const { addItem } = useCart();
  const [amount, setAmount] = useState(50);
  const [custom, setCustom] = useState("");
  const [to, setTo] = useState("");
  const [message, setMessage] = useState("");

  const value = custom ? Math.max(10, Number(custom) || 0) : amount;

  const addGiftCard = () => {
    addItem(
      {
        id: `gift-${value}`,
        slug: "gift-cards",
        name: `Gift Card — ${euro(value)}`,
        price: value,
        stock: 99,
        images: [SITE_IMAGES.giftCard],
      },
      1
    );
  };

  return (
    <div className="pt-24">
      <Seo
        title="Gift cards"
        description="Give the gift of choice — a Fancy Plants gift card, redeemable against anything in the shop. No expiry."
        path="/gift-cards"
      />
      <div className="container-fp grid items-center gap-12 py-12 lg:grid-cols-2">
        {/* Preview card */}
        <div className="order-2 lg:order-1">
          <div className="relative aspect-[1.6/1] overflow-hidden rounded-3xl bg-forest-900 p-8 text-canvas shadow-lift">
            <PlantImage
              src={SITE_IMAGES.giftCard}
              alt=""
              ratio="1.6/1"
              className="absolute inset-0 h-full w-full"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-r from-green-900/85 via-green-900/55 to-green-900/10" />
            <Gift size={28} className="relative z-10 text-brass" />
            <div className="relative z-10 mt-8">
              <p className="font-display text-2xl italic text-brass">Fancy Plants</p>
              <p className="font-display text-5xl">{euro(value)}</p>
              {to && <p className="mt-3 text-canvas/80">For {to}</p>}
              {message && <p className="mt-1 max-w-xs text-sm text-canvas/70">"{message}"</p>}
            </div>
            <p className="absolute bottom-6 left-8 z-10 text-xs text-canvas/60">
              Gift card · no expiry · redeemable against any plant
            </p>
          </div>
        </div>

        {/* Form */}
        <div className="order-1 lg:order-2">
          <p className="eyebrow mb-2">Gift cards</p>
          <h1 className="t-h1">The easiest plant gift in the world.</h1>
          <p className="t-lead mt-4">
            Let them choose their own fancy plant. No expiry, redeemable against anything in the
            shop — we'll set each one up by hand and arrange it by email.
          </p>

          <div className="mt-7 space-y-5">
            <div>
              <span className="mb-2 block text-sm font-medium">Amount</span>
              <div className="flex flex-wrap gap-2">
                {AMOUNTS.map((a) => (
                  <button
                    key={a}
                    onClick={() => { setAmount(a); setCustom(""); }}
                    className={`chip ${!custom && amount === a ? "chip-active" : ""}`}
                  >
                    {!custom && amount === a && <Check size={12} />} {euro(a)}
                  </button>
                ))}
                <input
                  type="number"
                  min="10"
                  value={custom}
                  onChange={(e) => setCustom(e.target.value)}
                  placeholder="Custom €"
                  className="w-28 rounded-full border border-line bg-canvas-raised px-4 py-1.5 text-sm focus:border-forest-500"
                />
              </div>
            </div>

            <label className="block">
              <span className="mb-1.5 block text-sm font-medium">Recipient's name (optional)</span>
              <input value={to} onChange={(e) => setTo(e.target.value)} className="fp-input" placeholder="e.g. Méabh" />
            </label>

            <label className="block">
              <span className="mb-1.5 block text-sm font-medium">A short message (optional)</span>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={2}
                maxLength={120}
                className="fp-input resize-none"
                placeholder="Happy birthday — go wild in the glasshouse."
              />
            </label>

            <button onClick={addGiftCard} className="btn-brass w-full sm:w-auto">
              Add {euro(value)} gift card to basket
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
